// Session 67: demo clients.
//
// Leena wants a client she can walk through on camera (Add client, Welcome,
// plan, questions, drafts) and use on sales calls, without a recording ever
// emailing a real person or publishing to a real site. The portal has no
// demo flag in the pack (portal_create_client and portal_update_client build
// the pack from a fixed set of keys, so an extra key would be dropped), and a
// migration is the owner's step. Until that lands, the marker is the name:
// a client whose name ends in "(Demo)" is a demo, and both admin screens
// hold it to the three rules below.
//
//   1. Every contact (notice, sign-in, approver) is a Digital Lotus address,
//      so invites, welcome emails, questions and drafts land with the agency.
//   2. Publishing stays on handoff: the package is delivered, nothing goes
//      live anywhere.
//   3. The "(Demo)" suffix stays, so the marker survives an edit.
//
// Everything else runs exactly as for a real client (pre-fill, strategy,
// plan, questions, drafts, analytics if connected), which is the point of
// the recording.

export const DEMO_SUFFIX = ' (Demo)';
export const AGENCY_DOMAIN = 'digitallotusmarketing.com';

export const DEMO_HINT = `Adds "(Demo)" to the name, keeps publishing on handoff so nothing goes live, and only accepts @${AGENCY_DOMAIN} addresses for contacts, so every invite, question and draft comes to the agency. Everything else runs as for a real client, which is what the recording shows.`;

// Session 68: the demo's client login. An agency login is never re-pointed
// at a client (the invite skips it), so the sign-in must be a separate
// address on the agency domain; a plus address lands in Leena's own inbox.
export const DEMO_SIGNIN_HINT = `Portal sign-in for the demo: a plus address on @${AGENCY_DOMAIN}, for example leena+demo@${AGENCY_DOMAIN}, never your own login. The password email lands in your inbox; set the password there and sign in with it in a private window to see the demo as the client. Fast start is on and the approval mode cannot be agency only, because on the call the client login approves the plan and the draft on screen.`;

// Session 68b: the first demo used Leena's own agency login as the sign-in.
// The invite never re-points an agency login at a client, so she stayed the
// agency user and the Welcome screen showed Digital Lotus's details under the
// demo's name. A demo's sign-in is a plus address on the agency domain
// (leena+demo@...), which lands in the same inbox but is its own login.
export const DEMO_SIGNIN_RULE = `The demo's sign-in must be a plus address on @${AGENCY_DOMAIN}, like leena+demo@${AGENCY_DOMAIN}, and not your own login: an agency login is never turned into a client login, so with it you would stay the agency and the Welcome screen would show Digital Lotus's details.`;

export const isPlusAddress = (e) => /^[^@\s]+\+[^@\s]+@/.test(String(e || '').trim());

// The sign-ins in `list` a demo refuses: not agency, not a plus address, or
// the signed-in user's own email.
export const badDemoSignins = (list, ownEmail) => {
  const own = String(ownEmail || '').trim().toLowerCase();
  return (list || []).map((e) => String(e || '').trim()).filter((e) => e && (!isAgencyEmail(e) || !isPlusAddress(e) || e.toLowerCase() === own));
};

export const isDemoName = (name) => /\(demo\)\s*$/i.test(String(name || ''));

export const withDemoSuffix = (name) => {
  const n = String(name || '').trim();
  return isDemoName(n) ? n : `${n}${DEMO_SUFFIX}`;
};

export const isAgencyEmail = (e) => String(e || '').trim().toLowerCase().endsWith(`@${AGENCY_DOMAIN}`);

// The addresses in `list` that are not the agency's (what a demo refuses).
export const nonAgencyEmails = (list) => (list || []).filter((e) => String(e || '').trim() && !isAgencyEmail(e));
